import {
  MIC_DEVICE_STORAGE_KEY,
  safeLocalStorageGet,
} from "./voice-assistant-storage";

export const VAD_SILENCE_THRESHOLD = 0.015;
export const VAD_SILENCE_DURATION_MS = 2600;
export const VAD_MAX_RECORDING_MS = 30_000;
export const VAD_CHECK_INTERVAL_MS = 100;
export const USER_VOLUME_EVENT = "fintheon:voice-cao-user-volume";

export function dispatchUserVoiceVolume(
  volume: number,
  isSpeaking: boolean,
): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(USER_VOLUME_EVENT, {
      detail: {
        volume: Math.max(0, Math.min(1, volume)),
        isSpeaking,
      },
    }),
  );
}

export function userVolumeFromRms(rms: number): number {
  return Math.max(0, Math.min(1, rms * 18));
}

export function selectedAudioConstraints(): MediaTrackConstraints {
  const selectedDeviceId = safeLocalStorageGet(MIC_DEVICE_STORAGE_KEY);
  if (!selectedDeviceId) {
    return { echoCancellation: true, noiseSuppression: true };
  }

  return {
    deviceId: { exact: selectedDeviceId },
    echoCancellation: true,
    noiseSuppression: true,
  };
}

const DEFAULT_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
};

function isMissingAudioInputError(error: unknown): boolean {
  const name =
    typeof error === "object" && error !== null && "name" in error
      ? String((error as { name?: unknown }).name ?? "")
      : "";
  if (
    name === "NotFoundError" ||
    name === "DevicesNotFoundError" ||
    name === "OverconstrainedError"
  ) {
    return true;
  }

  const message = error instanceof Error ? error.message : String(error ?? "");
  return /requested device not found|device not found|no audio input device/i.test(
    message,
  );
}

async function getExplicitAudioInputStream(
  excludedDeviceId?: string,
): Promise<MediaStream | null> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const candidates = devices.filter(
    (device) =>
      device.kind === "audioinput" &&
      device.deviceId &&
      device.deviceId !== "default" &&
      device.deviceId !== excludedDeviceId,
  );

  for (const device of candidates) {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: {
          ...DEFAULT_AUDIO_CONSTRAINTS,
          deviceId: { exact: device.deviceId },
        },
      });
    } catch (error) {
      if (!isMissingAudioInputError(error)) throw error;
    }
  }

  return null;
}

async function getDefaultOrExplicitAudioStream(
  excludedDeviceId?: string,
): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: DEFAULT_AUDIO_CONSTRAINTS,
    });
  } catch (error) {
    if (!isMissingAudioInputError(error)) throw error;
    const explicitStream = await getExplicitAudioInputStream(excludedDeviceId);
    if (explicitStream) {
      console.warn(
        "[VoiceAssistant] Default mic alias was unavailable; using a live audio input.",
      );
      return explicitStream;
    }
    throw error;
  }
}

export async function getAudioStream(): Promise<MediaStream> {
  const audioConstraints = selectedAudioConstraints();
  if (!("deviceId" in audioConstraints)) {
    return getDefaultOrExplicitAudioStream();
  }
  const selectedDeviceId =
    typeof audioConstraints.deviceId === "object" &&
    !Array.isArray(audioConstraints.deviceId) &&
    typeof audioConstraints.deviceId.exact === "string"
      ? audioConstraints.deviceId.exact
      : undefined;

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints,
    });
  } catch (error) {
    if (!isMissingAudioInputError(error)) throw error;
    try {
      localStorage.removeItem(MIC_DEVICE_STORAGE_KEY);
    } catch {
      // Storage can be blocked in private or embedded contexts.
    }
    console.warn(
      "[VoiceAssistant] Selected mic failed, falling back to available input:",
      error instanceof Error ? error.message : String(error),
    );
    return getDefaultOrExplicitAudioStream(selectedDeviceId);
  }
}

export function createAudioContext(): AudioContext {
  const win = window as typeof window & {
    webkitAudioContext?: typeof AudioContext;
  };
  const AudioContextCtor = window.AudioContext || win.webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error("AudioContext unavailable");
  }
  return new AudioContextCtor();
}

export function createRecordingMediaRecorder(
  stream: MediaStream,
): MediaRecorder | null {
  if (typeof MediaRecorder === "undefined") return null;
  const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
    ? "audio/webm;codecs=opus"
    : "audio/webm";
  return new MediaRecorder(stream, { mimeType });
}
